import React, { useEffect, useMemo, useState } from "react";
import { Box, Paper, Typography, Chip, IconButton, Collapse, Table, TableHead, TableRow, TableCell, TableBody, Button, Grid, useTheme, useMediaQuery } from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CloseIcon from '@mui/icons-material/Close';
import ImageIcon from '@mui/icons-material/Image';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getAllPostMonthlyTargetsAction, getAllPostActivitiesAction, getAllPostMediaFilesAction } from "../../../store/actions";


const ViewPostActivity = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const postActivityData = location.state?.postActivityData;
  const [expandedMonthId, setExpandedMonthId] = useState(null);
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.only("xs"));

  // State for image dialog
  const [openImageDialog, setOpenImageDialog] = useState(false);
  const [selectedActivityId, setSelectedActivityId] = useState(null);

  // Selector for media files
  const { successData: mediaFilesData, loading: mediaFilesLoading } = useSelector(
    (state) => state.getAllPostMediaFilesReducer || {}
  );

  const { successData: monthlyTargetsData, loading: monthlyTargetsLoading } = useSelector(
    (state) => state.getAllPostMonthlyTargetsReducer || {}
  );
  // Selector for post-activities
  // const { successData: activitiesData, loading: activitiesLoading } = useSelector(
  //   (state) => state.getAllPostActivityReducer || {}
  const { successData: activitiesData, loading: activitiesLoading } = useSelector(
    (state) => state.getAllPostActivityReducer || {}
  );

  console.log("activitiesData", activitiesData);


  
  // Open image dialog and dispatch action
  const handleOpenImages = (activityId) => {
    setSelectedActivityId(activityId);
    setOpenImageDialog(true);
    dispatch(getAllPostMediaFilesAction({
      endPoint: `activity-media/post?activityId=${activityId}`
    }));
  };

  // Close image dialog
  const handleCloseImages = () => {
    setOpenImageDialog(false);
    setSelectedActivityId(null);
  };
  

  useEffect(() => {
    if (postActivityData) {
      dispatch(getAllPostMonthlyTargetsAction());
      dispatch(getAllPostActivitiesAction());
    }
  }, [dispatch, postActivityData]);

  // Filter monthly targets by post_target_id and show all months
  const filteredMonthlyTargets = useMemo(() => {
    if (!postActivityData || !monthlyTargetsData) return [];
    let monthlyTargetsList = monthlyTargetsData?.data || [];
    if (!Array.isArray(monthlyTargetsList)) return [];
    return monthlyTargetsList.filter((target) => {
      const targetPostTargetId = target.post_target_id ?? target.postTargetId ?? target.postTarget?.id;
      return String(targetPostTargetId) === String(postActivityData.id);
    });
  }, [postActivityData, monthlyTargetsData]);

  // Get current year and month in YYYY-MM format
  const now = new Date();
  const currentMonthYear = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

  // Get activities for each month (filter by post_target_id and post_month_id)
  const getActivitiesForMonth = (monthId) => {
    let activitiesList = [];
    if (activitiesData?.activities) {
      activitiesList = activitiesData.activities;
    } else if (activitiesData?.data?.activities) {
      activitiesList = activitiesData.data.activities;
    } else if (Array.isArray(activitiesData?.data)) {
      activitiesList = activitiesData.data;
    } else if (Array.isArray(activitiesData)) {
      activitiesList = activitiesData;
    }
    if (!Array.isArray(activitiesList)) return [];
    const postTargetId = Number(postActivityData.id);
    const postMonthId = Number(monthId);
    return activitiesList.filter(activity => {
      const activityPostTargetId = Number(activity.post_target_id ?? activity.postTargetId);
      const activityPostMonthId = Number(activity.post_month_id ?? activity.postMonthId);
      return (
        activityPostTargetId === postTargetId &&
        activityPostMonthId === postMonthId
      );
    });
  };

  const handleToggleActivities = (monthId) => {
    setExpandedMonthId(expandedMonthId === monthId ? null : monthId);
  };

  if (!postActivityData) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",  }}>
        <Typography variant="h6" color="textSecondary">
          No post-season activity data found.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", p: 3, mt: { xs: 7, sm: 0 } }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={600}>
          Post-Season Activity Details
        </Typography>
        <IconButton onClick={() => navigate(-1)}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Post-Season Activity Details Card - Responsive for xs */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3, position: "relative" }}>
        {isXs ? (
          <Box sx={{ p: 1, minWidth: 220, minHeight: 180, position: "relative" }}>
            {/* Status Chip in top right */}
            <Chip
              label={(postActivityData?.is_active ?? postActivityData?.isActive) ? "Active" : "Inactive"}
              size="small"
              sx={{
                position: "absolute",
                top: 12,
                right: 12,
                backgroundColor: (postActivityData?.is_active ?? postActivityData?.isActive) ? '#D1FAE5' : '#FECACA',
                color: (postActivityData?.is_active ?? postActivityData?.isActive) ? '#047857' : '#B91C1C',
                fontWeight: 600,
                fontSize: 12,
              }}
            />
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                <span style={{ color: "#374151" }}>Season:</span> {postActivityData?.season?.name}{postActivityData?.season?.year ? ` (${postActivityData.season.year})` : ""}
              </Typography>
              <Typography variant="body2">
                <span style={{ color: "#374151" }}>Area Type:</span> {postActivityData?.target_area_type}
              </Typography>
              <Typography variant="body2">
                <span style={{ color: "#374151" }}>Dealer visit:</span> {postActivityData?.dv_done_count ?? postActivityData?.dvDoneCount ?? 0}/{postActivityData?.dv_count ?? postActivityData?.dvCount ?? 0}
              </Typography>
              <Typography variant="body2">
                <span style={{ color: "#374151" }}>Field Day:</span> {postActivityData?.fd_done_count ?? postActivityData?.fdDoneCount ?? 0}/{postActivityData?.fd_count ?? postActivityData?.fdCount ?? 0}
              </Typography>
              <Typography variant="body2">
                <span style={{ color: "#374151" }}>Field Visit:</span> {postActivityData?.fv_done_count ?? postActivityData?.fvDoneCount ?? 0}/{postActivityData?.fv_count ?? postActivityData?.fvCount ?? 0}
              </Typography>
            </Box>
            {/* Action icons at bottom right (optional, can add if needed) */}
          </Box>
        ) : (
          <>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Activity Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" color="textSecondary">Activity ID</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>{postActivityData?.id}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" color="textSecondary">Season</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>{postActivityData?.season?.name}{postActivityData?.season?.year ? ` (${postActivityData.season.year})` : ""}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" color="textSecondary">Target Area Type</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>{postActivityData?.target_area_type}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" color="textSecondary">Dealer Visit</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>{postActivityData?.dv_done_count ?? postActivityData?.dvDoneCount ?? 0}/{postActivityData?.dv_count ?? postActivityData?.dvCount ?? 0}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" color="textSecondary">Field Day</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>{postActivityData?.fd_done_count ?? postActivityData?.fdDoneCount ?? 0}/{postActivityData?.fd_count ?? postActivityData?.fdCount ?? 0}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" color="textSecondary">Field Visit</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>{postActivityData?.fv_done_count ?? postActivityData?.fvDoneCount ?? 0}/{postActivityData?.fv_count ?? postActivityData?.fvCount ?? 0}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2" color="textSecondary">Status</Typography>
                <Chip
                  label={(postActivityData?.is_active ?? postActivityData?.isActive) ? "Active" : "Inactive"}
                  size="small"
                  sx={{
                    mt: 0.5,
                    backgroundColor: (postActivityData?.is_active ?? postActivityData?.isActive) ? '#D1FAE5' : '#FECACA',
                    color: (postActivityData?.is_active ?? postActivityData?.isActive) ? '#047857' : '#B91C1C',
                    fontWeight: 600
                  }}
                />
              </Grid>
            </Grid>
          </>
        )}
      </Paper>

      {/* Monthly Targets Section */}
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        Monthly Targets
      </Typography>
      {monthlyTargetsLoading ? (
        <Typography>Loading monthly targets...</Typography>
      ) : filteredMonthlyTargets.length > 0 ? (
        <Box>
          {filteredMonthlyTargets.map((target) => {
            const activities = getActivitiesForMonth(target.id);
            const isExpanded = expandedMonthId === target.id;
            const monthYear = target.month_year || target.monthYear;
            // Normalize monthYear to YYYY-MM
            let normalizedMonthYear = "";
            if (/^\d{4}-\d{1,2}$/.test(monthYear)) {
              normalizedMonthYear = monthYear;
            } else if (/^\d{1,2}-\d{4}$/.test(monthYear)) {
              const [m, y] = monthYear.split('-');
              normalizedMonthYear = `${y}-${m.padStart(2, '0')}`;
            }
            const showAddActivity = normalizedMonthYear === currentMonthYear;
            return (
              <Paper 
                key={target.id} 
                elevation={0} 
                sx={{ 
                  mb: 2, 
                  borderRadius: 3, 
                  border: "1px solid #E5E7EB",
                  overflow: "hidden"
                }}
              >
                {/* Monthly Target Row */}
                {isXs ? (
                  <Box sx={{ p: 1.5, minWidth: 200 }}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        <span style={{ color: "#374151" }}>Month ID:</span> {target.id}
                      </Typography>
                      <Typography variant="body2">
                        <span style={{ color: "#374151" }}>Month:</span> {monthYear}
                      </Typography>
                      <Typography variant="body2">
                        <span style={{ color: "#374151" }}>Dealer Visit:</span> {target.dv_done_count ?? target.dvDoneCount ?? 0}/{target.dv_count ?? target.dvCount ?? 0}
                      </Typography>
                      <Typography variant="body2">
                        <span style={{ color: "#374151" }}>Field Visit:</span> {target.fv_done_count ?? target.fvDoneCount ?? 0}/{target.fv_count ?? target.fvCount ?? 0}
                      </Typography>
                      <Typography variant="body2">
                        <span style={{ color: "#374151" }}>Field Day:</span> {target.fd_done_count ?? target.fdDoneCount ?? 0}/{target.fd_count ?? target.fdCount ?? 0}
                      </Typography>
                    </Box>
                    {/* Action: Add Activity only for current month, View Activities for all */}
                    <Box sx={{ display: "flex", flexDirection: "row", gap: 0.5, alignItems: "center", mt: 1.5 }}>
                      {showAddActivity && (
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          sx={{ borderRadius: 2, fontSize: 11, py: 0.3, px: 1 }}
                          onClick={() => navigate('/SOorMDO/createPostActivity', { state: { monthId: target.id, postTargetId: postActivityData.id } })}
                        >
                          Add Activity
                        </Button>
                      )}
                      {activities.length > 0 && (
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          sx={{ borderRadius: 2, fontSize: 11, py: 0.3, px: 1 }}
                          onClick={() => handleToggleActivities(target.id)}
                        >
                          {isExpanded ? "Hide" : "View Activities"}
                        </Button>
                      )}
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}>
                    <Box sx={{ flex: 1, display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 2, alignItems: "center" }}>
                      <Box>
                        <Typography variant="caption" color="textSecondary">Month ID</Typography>
                        <Typography variant="body2" fontWeight={600}>{target.id}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="textSecondary">Month</Typography>
                        <Typography variant="body2" fontWeight={600}>{monthYear}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="textSecondary">Dealer Visit</Typography>
                        <Typography variant="body2" fontWeight={600}>{target.dv_done_count ?? target.dvDoneCount ?? 0}/{target.dv_count ?? target.dvCount ?? 0}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="textSecondary">Field Visit</Typography>
                        <Typography variant="body2" fontWeight={600}>{target.fv_done_count ?? target.fvDoneCount ?? 0}/{target.fv_count ?? target.fvCount ?? 0}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="textSecondary">Field Day</Typography>
                        <Typography variant="body2" fontWeight={600}>{target.fd_done_count ?? target.fdDoneCount ?? 0}/{target.fd_count ?? target.fdCount ?? 0}</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "row", gap: 0.5, alignItems: "center" }}>
                      {showAddActivity && (
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          sx={{ borderRadius: 2, fontSize: 11, py: 0.4, px: 1.5 }}
                          onClick={() => navigate('/SOorMDO/createPostActivity', { state: { monthId: target.id, postTargetId: postActivityData.id } })}
                        >
                          Add Activity
                        </Button>
                      )}
                      {activities.length > 0 && (
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          sx={{ borderRadius: 2, fontSize: 11, py: 0.4, px: 1.5 }}
                          onClick={() => handleToggleActivities(target.id)}
                        >
                          {isExpanded ? "Hide" : "View Activities"}
                        </Button>
                      )}
                    </Box>
                  </Box>
                )}

                {/* Activities Table - Collapsible */}
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <Box sx={{ px: 2, pb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: "#4B5563" }}>
                      Associated Activities
                    </Typography>
                    {/* Show loading, activities, or fallback */}
                    {activitiesLoading ? (
                      <Typography variant="body2" color="textSecondary" sx={{ py: 2, textAlign: "center" }}>
                        Loading activities...
                      </Typography>
                    ) : activities.length > 0 ? (
                       isXs ? (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                          {activities.map((activity) => (
                            <Paper
                              key={activity.id}
                              elevation={0}
                              sx={{
                                border: "1px solid #E5E7EB",
                                borderRadius: 2,
                                p: 1.5,
                                position: "relative",
                                minWidth: 200,
                                minHeight: 110,
                              }}
                            >
                              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  <span style={{ color: "#374151" }}>Activity ID:</span> {activity.id}
                                </Typography>
                                <Typography variant="body2">
                                  <span style={{ color: "#374151" }}>Village ID:</span> {activity.villageId || activity.village_id}
                                </Typography>
                                <Typography variant="body2">
                                  <span style={{ color: "#374151" }}>Activity Name:</span> {activity.activityName || activity.activity_name}
                                </Typography>
                                <Typography variant="body2">
                                  <span style={{ color: "#374151" }}>Start Time:</span> {new Date(activity.startTime || activity.start_time).toLocaleTimeString('en-US', { 
                                    hour: '2-digit', 
                                    minute: '2-digit' 
                                  })}
                                </Typography>
                                <Typography variant="body2">
                                  <span style={{ color: "#374151" }}>End Time:</span> {new Date(activity.endTime || activity.end_time).toLocaleTimeString('en-US', {  hour: '2-digit', 
                                    minute: '2-digit' 
                                  })}
                                </Typography>
                                <Typography variant="body2">
                                  <span style={{ color: "#374151" }}>No. of Farmers:</span> {activity.noOfFarmers || activity.no_of_farmers}
                                </Typography>   
                              </Box>
                              <Box sx={{ position: "absolute", bottom: 8, right: 8, display: "flex", gap: 1 }}>
                                <IconButton
                                  color="primary"
                                  size="small"
                                  onClick={() => handleOpenImages(activity.id)}
                                >
                                  <ImageIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </Paper>
                          ))}
                        </Box>
                      ) : (
                        <Paper elevation={0} sx={{ border: "1px solid #E5E7EB", borderRadius: 2 }}>
                          <Table size="small">
                            <TableHead sx={{ backgroundColor: "#F9FAFB" }}>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Activity ID</TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Village ID</TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Activity Name</TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Start Time</TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>End Time</TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>No. of Farmers</TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Actions</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {activities.map((activity) => (
                                <TableRow key={activity.id}>
                                  <TableCell>{activity.id}</TableCell>
                                  <TableCell>{activity?.village?.name}</TableCell>
                                  <TableCell>{activity.activityName || activity.activity_name}</TableCell>
                                  <TableCell>
                                    {new Date(activity.startTime || activity.start_time).toLocaleTimeString('en-US', {
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </TableCell>
                                  <TableCell>
                                    {new Date(activity.endTime || activity.end_time).toLocaleTimeString('en-US', {
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </TableCell>
                                  <TableCell>{activity.noOfFarmers || activity.no_of_farmers}</TableCell>
                                  <TableCell>
                                    <Box sx={{ display: "flex", gap: 1 }}>
                                      <IconButton
                                        color="primary"
                                        size="small"
                                        onClick={() => handleOpenImages(activity.id)}
                                      >
                                        <ImageIcon fontSize="small" />
                                      </IconButton>
                                            {/* Image Dialog */}
                                            {openImageDialog && (
                                              <Box
                                                sx={{
                                                  position: 'fixed',
                                                  top: 0,
                                                  left: 0,
                                                  width: '100vw',
                                                  height: '100vh',
                                                  bgcolor: 'rgba(0,0,0,0.5)',
                                                  zIndex: 2000,
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                }}
                                                onClick={handleCloseImages}
                                              >
                                                <Paper
                                                  elevation={3}
                                                  sx={{
                                                    p: 3,
                                                    borderRadius: 3,
                                                    minWidth: 320,
                                                    minHeight: 200,
                                                    maxWidth: '90vw',
                                                    maxHeight: '90vh',
                                                    overflowY: 'auto',
                                                    position: 'relative',
                                                  }}
                                                  onClick={e => e.stopPropagation()}
                                                >
                                                  <IconButton
                                                    onClick={handleCloseImages}
                                                    sx={{ position: 'absolute', top: 8, right: 8 }}
                                                  >
                                                    <CloseIcon />
                                                  </IconButton>
                                                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                                    Activity Images
                                                  </Typography>
                                                  {mediaFilesLoading ? (
                                                    <Typography>Loading images...</Typography>
                                                  ) : mediaFilesData && Array.isArray(mediaFilesData.data) && mediaFilesData.data.length > 0 ? (
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                                      {mediaFilesData.data.map((file, idx) => (
                                                        <Box key={idx} sx={{ width: 120, height: 120, borderRadius: 2, overflow: 'hidden', border: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#F9FAFB' }}>
                                                          <img
                                                            src={file.url || file.fileUrl || file.file_url}
                                                            alt={file.name || `Activity Image ${idx + 1}`}
                                                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                                          />
                                                        </Box>
                                                      ))}
                                                    </Box>
                                                  ) : (
                                                    <Typography color="textSecondary">No images found for this activity.</Typography>
                                                  )}
                                                </Paper>
                                              </Box>
                                            )}
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Paper>
                      )
                    ) : (
                      <Typography variant="body2" color="textSecondary" sx={{ py: 2, textAlign: "center" }}>
                        No activities found.
                      </Typography>
                    )}
                  </Box>
                </Collapse>
              </Paper>
            );
          })}
        </Box>
      ) : (
        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #E5E7EB", textAlign: "center" }}>
          <Typography color="textSecondary">No monthly targets found for this post-season activity.</Typography>
        </Paper>
      )}

      {/* Back Button */}
      <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          color="primary"
          sx={{ borderRadius: 2 }}
          onClick={() => navigate(-1)}
        >
          Back to Post-Activities
        </Button>
      </Box>
    </Box>
  );
};

export default ViewPostActivity;